import { isDefined, isEmpty, isNonEmpty, isNotDefined } from '@sgme/fp';
import { Checkbox, Dropdown } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { userApi } from '@/api/user/index.ts';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import { PredealChecksIndicator } from '@/App/tiles/shared/PredealChecksIndicator.tsx';
import { copyToClipBoardThunk } from '@/App/tiles/Swap/copyToClipBoardThunk.ts';
import { useCheckSwapByTradeCapture } from '@/App/tiles/Swap/SwapContext.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectSwapPrices } from '@/store/swap/selectors/rfs.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { swapSlice } from '@/store/swap/slice.ts';
import type { FxSwapNds } from '@/store/swap/state.ts';
import { resetSwap } from '@/store/swap/thunks/resetSwap.ts';
import { duplicateTile, removeTile } from '@/store/tiles/slice.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import type { ObjectWithUndefinedPropertiesNullable } from '@/utils/typescript.ts';

export type SwapHeaderProps = {
  tileId: string;
};

export const SwapHeader = (props: SwapHeaderProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();
  const { defaultNotional } = useAppSelector(getUserPreferences);

  const dropdownToggleRef = useRef<HTMLButtonElement>(null);

  const { currencyPair, clientId, predealChecks, globalPreDealCheck, isNonDeliverable, isNdsVisible, isNdsEditable } =
    useAppSelector((state) => {
      const tile = selectSwapTile(state, tileId);

      if (isNotDefined(tile)) {
        return { globalPreDealCheck: 'Unknown', isNonDeliverable: false, isNdsVisible: false, isNdsEditable: false };
      }

      const predealChecks =
        tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote.predealChecks : undefined;

      const globalPreDealCheck = isNotDefined(predealChecks)
        ? 'Unknown'
        : predealChecks.filter((check) => check.type === 'Icon' && check.statusHealth === 'Nok').length > 0
          ? 'Nok'
          : 'Ok';

      const currentUser = userApi.endpoints.currentUser.select()(state).data;
      const currencyPairData = selectCurrencyPairData(state, tile.properties.currencyPair);
      const canDealNonDeliverable = currentUser?.productsAccess.nonDeliverable ?? false;
      const isNdsVisible = canDealNonDeliverable && (currencyPairData?.notDeliverable ?? false);

      // NDS is editable if it can be delivered, OR if one of its currencies is a local market currency
      // (mirrors fx-web/app/src/state/referenceData/referenceDataReducer.ts -> updateCcyPairsWithLocalMarket)
      const localMarketCurrencies = currentUser?.localMarketsCcy ?? [];

      const isNdsEditable =
        (currencyPairData?.canBeDelivered ?? false) ||
        (isDefined(tile.properties.currency1) && localMarketCurrencies.includes(tile.properties.currency1)) ||
        (isDefined(tile.properties.currency2) && localMarketCurrencies.includes(tile.properties.currency2));

      return {
        currencyPair: tile.properties.currencyPair,
        clientId: tile.clientId,
        predealChecks,
        globalPreDealCheck,
        isNonDeliverable: tile.isNonDeliverable,
        isNdsVisible,
        isNdsEditable,
      };
    }, deepEqual);

  const prices = useAppSelector((state) => selectSwapPrices(state, tileId));

  const checkSwapWithTradeCapture = useCheckSwapByTradeCapture();

  const handleToggleNds = useCallback(() => {
    checkSwapWithTradeCapture({}, undefined, !isNonDeliverable);
  }, [checkSwapWithTradeCapture, isNonDeliverable]);

  const handleCurrencyPairChange = useCallback(
    (value: string) => {
      if (value === '') {
        // don't do anything if currency pair is cleared
        return;
      }

      const defaultNotionalValue: ObjectWithUndefinedPropertiesNullable<FxSwapNds['properties']> = isDefined(
        defaultNotional,
      )
        ? {
            farNotional: Number(defaultNotional),
            farNotionalCurrencyIndex: 1,
            nearNotional: Number(defaultNotional),
            nearNotionalCurrencyIndex: 1,
          }
        : {};

      sgwtWebAnalytics?.trackEvent('Swap', 'Currency pair changed', value);

      checkSwapWithTradeCapture({
        currencyPair: value,
        ...defaultNotionalValue,
      });
    },
    [checkSwapWithTradeCapture, defaultNotional, sgwtWebAnalytics],
  );

  const handleDuplicate = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile duplicated', tileId);

    dispatch(
      duplicateTile({
        sourceTileId: tileId,
        newTileId: uuid(),
        sessionId: uuid(),
      }),
    );
  };

  const handleReset = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile reseted', tileId);
    dispatch(resetSwap(tileId));
  };

  const handleClose = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile closed', tileId);
    dispatch(removeTile(tileId));
  };

  const handleCopyToClipboard = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile copied to clipboard', tileId);
    dispatch(copyToClipBoardThunk(tileId));
  };

  const handleSelectClient = (clientId: number) => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile client selected', String(clientId));
    dispatch(swapSlice.actions.swapClientIdUpdated({ tileId, clientId, isTabClientOverriden: true }));
  };

  const handleClearSelectedClient = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'Tile client reseted', tileId);
    dispatch(swapSlice.actions.swapClientIdUpdated({ tileId, clientId: undefined, isTabClientOverriden: true }));
  };

  return (
    <>
      <TileOverridenClientIndicator clientId={clientId} onRemoveOverride={handleClearSelectedClient} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ToggleProduct tileId={tileId} />
          <PredealChecksIndicator predealChecks={predealChecks} globalPreDealCheck={globalPreDealCheck} />
        </div>
        <CurrencyPairPicker
          tradeType="Swap"
          value={currencyPair}
          onChange={handleCurrencyPairChange}
          data-e2e="currency-pair-input"
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          {isNonEmpty(currencyPair) && isNdsVisible && (
            <Checkbox
              label="NDS"
              checked={isNonDeliverable}
              disabled={!isNdsEditable}
              onClick={handleToggleNds}
              dataE2e="nds-checkbox"
            />
          )}

          <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
            <Dropdown.Toggle className="btn-icon" data-e2e="menu-button">
              <i className="icon" ref={dropdownToggleRef}>
                more_vert
              </i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />
              <Dropdown.Item
                onClick={handleDuplicate}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="duplicate-tile-menu"
              >
                Duplicate
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleReset}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="reset-tile-menu"
              >
                Reset
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleCopyToClipboard}
                state={isEmpty(currencyPair) || isNotDefined(prices) ? 'disabled' : undefined}
                data-e2e="copyToClipboard-tile-menu"
              >
                Copy to clipboard
              </Dropdown.Item>
              <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
                Close
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </>
  );
};
